import {
  Component,
  computed,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { TranslateDirective } from '@ngx-translate/core';

import { SelectionChangeEvent } from '../../domain/selectable.domain';
import { IconComponent } from '../icon/icon.component';

import type {
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal,
  WritableSignal
} from '@angular/core';
/**
 * The Chip type will define its icon
 * Available values: NONE, ADD, REMOVE
 */
export type ChipIconType = 'add' | 'remove' | 'none';

export type StyleType = 'solid-grey' | 'outline' | 'solid-white';

export type ChipIconPosition = 'left' | 'right';

@Component({
  imports: [
    IconComponent,
    TranslateDirective
  ],
  selector: 'td-wb-chip',
  templateUrl: 'chip.component.html',
  styleUrls: ['chip.component.scss'],
  host: {
    '[attr.id]': 'id()',
    '[class.td-wb-chip--selected]': 'selected',
    '[class.td-wb-chip--hover]': 'hover()',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()'
  }
})
export class ChipComponent {
  private _selected: WritableSignal<boolean> = signal(false);
  protected hover: WritableSignal<boolean> = signal(false);

  item: InputSignal<unknown | undefined> = input<unknown>();
  id: ModelSignal<string | undefined> = model<string>();
  name: ModelSignal<string | undefined> = model<string>();
  iconType: InputSignal<ChipIconType> = input<ChipIconType>('none');
  styleType: InputSignal<StyleType> = input<StyleType>('outline');
  iconPosition: InputSignal<ChipIconPosition> = input<ChipIconPosition>('right');
  disabled: ModelSignal<boolean> = model<boolean>(false);

  get selected(): boolean {
    return this._selected();
  }

  set selected(value: boolean) {
    this.setCheckedState(value, false);
  }

  setSelected(value: boolean): void {
    this._selected.set(value); // skip emit back to parent
  }

  action: OutputEmitterRef<SelectionChangeEvent<ChipComponent>> = output<SelectionChangeEvent<ChipComponent>>();

  protected onMouseEnter(): void {
    this.hover.set(true);
  }

  protected onMouseLeave(): void {
    this.hover.set(false);
  }

  protected onButtonClick(event: Event): false {
    if(this.disabled()) {
      event.stopPropagation();
      return false;
    }
    this.setCheckedState(!this._selected(), true);
    return false;
  }

  protected hasIcon: Signal<boolean> = computed(() => this.iconType() !== 'none');

  protected isIconRightPositioned: Signal<boolean> = computed(() => this.hasIcon() && this.iconPosition() === 'right');

  protected isIconLeftPositioned: Signal<boolean> = computed(() => this.hasIcon() && this.iconPosition() === 'left');

  // maps enum to an icon
  protected getIcon: Signal<string> = computed(() => {
    switch(this.iconType()) {
      case 'remove': return 'clear';
      case 'add': return 'plus';
    }
    return '';
  });

  protected getAriaLabel: Signal<string> = computed(() => {
    const chipNameDiv = `td-wb-aria-filter-name-${this.id()}`;
    let contextName: string;
    switch(this.iconType()) {
      case 'remove':
        contextName = `td-wb-aria-remove-label-${this.id()}`
        break;
      case 'add':
        contextName = `td-wb-aria-select-label-${this.id()}`
        break;
      case 'none':
        contextName = `td-wb-aria-filters-${this.id()}`
        break;
      default:
        contextName = '';
        break;
    }
    return `${chipNameDiv} ${contextName}`;
  });

  protected getClasses: Signal<Array<string>> = computed(() => [
    'td-wb-chip__item',
    'td-wb-chip--' + this.styleType(),
    'td-wb-chip--' + this.iconType(),
    this.disabled() ? 'td-wb-chip--disabled' : ''
  ]);

  private setCheckedState(newCheckedState: boolean, fromView: boolean): void {
    if (this._selected() !== newCheckedState) {
      this._selected.set(newCheckedState);
      // Communicate to the parent that there was an action on this component, so it can coordinate accordingly
      this.action.emit(new SelectionChangeEvent(this, fromView));
    }
  }
}
